import fromTypes from "../actions/types";
import * as fromOrganisations from "features/organisations";
import * as fromSites from "features/sites/actions/types";
import {
	onSiteInspectionChecklistListReceive,
	onSiteInspectionChecklistListFailed,
} from "../actions";
import { ofType } from "redux-observable";
import { mergeMap, catchError, filter, switchMap, delay, map } from "rxjs/operators";
import { ajax } from "rxjs/ajax";
import { of } from "rxjs";
import { authHeaders, tokenExists, getOrganisationBaseUrl } from "frame";

// Debug: log action type constants being used
console.log('Action types used in orgSiteInspectionChecklistEffect:');
console.log('- SITE_INSPECTION_CHECKLIST_LIST_REQUESTED:', fromTypes.SITE_INSPECTION_CHECKLIST_LIST_REQUESTED);
console.log('- SITE_INSPECTION_CHECKLIST_PAGED_LIST_REQUESTED:', fromTypes.SITE_INSPECTION_CHECKLIST_PAGED_LIST_REQUESTED);
console.log('- SITE_INSPECTION_CHECKLIST_LIST_RECEIVED:', fromTypes.SITE_INSPECTION_CHECKLIST_LIST_RECEIVED);
console.log('- SITE_INSPECTION_CHECKLIST_PAGED_LIST_SUCCEEDED:', fromTypes.SITE_INSPECTION_CHECKLIST_PAGED_LIST_SUCCEEDED);
console.log('- SITE_INSPECTION_CHECKLIST_LIST_FAILED:', fromTypes.SITE_INSPECTION_CHECKLIST_LIST_FAILED);
console.log('- SITE_INSPECTION_CHECKLIST_PAGED_LIST_FAILED:', fromTypes.SITE_INSPECTION_CHECKLIST_PAGED_LIST_FAILED);

// Debug helper function
const logStateStructure = (state) => {
	console.log('State structure check:');
	console.log('- state.app exists:', !!state.app);
	if (state.app) {
		console.log('- state.app.currentOrganisation exists:', !!state.app.currentOrganisation);
		if (state.app.currentOrganisation) {
			console.log('- state.app.currentOrganisation.id:', state.app.currentOrganisation.id);
		}
	}
	console.log('- state.organisations exists:', !!state.organisations);
	if (state.organisations) {
		console.log('- state.organisations.currentOrganisation exists:', !!state.organisations.currentOrganisation);
		if (state.organisations.currentOrganisation) {
			console.log('- state.organisations.currentOrganisation.id:', state.organisations.currentOrganisation.id);
		}
	}
};

// This effect gets ALL site inspection checklists for an organization (across all sites)
export const orgSiteInspectionChecklistEffect = (action$, state$) => {
	console.log('orgSiteInspectionChecklistEffect initialized');
	
	return action$.pipe(
		ofType(
			fromTypes.SITE_INSPECTION_CHECKLIST_LIST_REQUESTED,
			fromOrganisations.ORGANISATIONS_CURRENT_ORGANISATION_CHANGED,
			fromSites.SITES_LIST_RECEIVED,
			fromSites.SITES_DETAILS
		),
		// Add a small delay to ensure state is populated
		delay(300),
		filter(action => {
			console.log('SITE_INSPECTION_CHECKLIST_LIST_REQUESTED action received:', action);
			
			// Log state structure for debugging
			if (state$ && state$.value) {
				logStateStructure(state$.value);
			}
			
			// More robust token check - log but don't block
			const token = localStorage.getItem("token");
			console.log('Token value exists:', !!token);

			// Check organization in both possible locations
			let organisationId = null;
			let currentOrganisation = null;
			
			if (state$.value.app && state$.value.app.currentOrganisation) {
				currentOrganisation = state$.value.app.currentOrganisation;
				console.log('Found organization in state.app.currentOrganisation');
			} else if (state$.value.organisations && state$.value.organisations.currentOrganisation) {
				currentOrganisation = state$.value.organisations.currentOrganisation;
				console.log('Found organization in state.organisations.currentOrganisation');
				
				// If we have currentOrganisation but id is undefined, try to use GUID instead
				if (currentOrganisation && !currentOrganisation.id && currentOrganisation.guid) {
					console.log('Using organization GUID instead of ID:', currentOrganisation.guid);
					currentOrganisation.id = currentOrganisation.guid;
				}
			}
			
			if (!currentOrganisation || !currentOrganisation.id) {
				console.log('Current organisation missing or has no ID - skipping request');
				return false;
			}

			if (!navigator.onLine) {
				console.log('No network connection - skipping request');
				return false;
			}

			console.log('All conditions passed - proceeding with request');
			return true;
		}),
		filter(() => {
			if (!state$ || !state$.value) {
				console.log("EFFECT: State not available, skipping checklist request");
				return false;
			}
			return true;
		}),
		switchMap(() => {
			console.log("EFFECT: Processing organization checklist request...");
			
			// Get organization ID using our comprehensive approach
			let organisationId = null;
			
			// Check app.currentOrganisation first
			if (state$.value.app && state$.value.app.currentOrganisation) {
				const org = state$.value.app.currentOrganisation;
				if (org.id) {
					organisationId = org.id;
				} else if (org.guid) {
					organisationId = org.guid;
				}
			} 
			
			// If not found, check organisations.currentOrganisation
			if (!organisationId && state$.value.organisations && state$.value.organisations.currentOrganisation) {
				const org = state$.value.organisations.currentOrganisation;
				
				// If it's a string (GUID), use it directly
				if (typeof org === 'string') {
					organisationId = org;
				}
				// If it's an object with ID, use that
				else if (org && org.id) {
					organisationId = org.id;
				}
				// If it has a GUID but no ID, use the GUID
				else if (org && org.guid) {
					organisationId = org.guid;
				}
			}
			
			if (!organisationId) {
				console.warn("EFFECT WARNING: No current organization ID available, returning empty results");
				return of(
					onSiteInspectionChecklistListReceive({
						results: [],
						totalNumberOfRecords: 0,
						totalNumberOfPages: 0,
						pageNumber: 1,
						pageSize: 0,
					})
				);
			}
			
			console.log(`EFFECT: Using organization ID/GUID: ${organisationId}`);
			
			// Check for network
			if (!state$.value.queue || !state$.value.queue.hasNetwork) {
				console.log("EFFECT: Network not available, returning empty results");
				return of(
					onSiteInspectionChecklistListReceive({
						results: [],
						totalNumberOfRecords: 0,
						totalNumberOfPages: 0,
						pageNumber: 1,
						pageSize: 0,
					})
				);
			}
			
			// Add timestamp to prevent caching
			const timestamp = new Date().getTime();
			
			// Use organization-wide endpoint without site ID
			console.log("EFFECT: Using organization-wide endpoint without site ID");
			
			// Use standard headers without cache-control
			const headers = {
				...authHeaders()
			};
			
			const orgWideRequestUrl = `${getOrganisationBaseUrl(
				state$.value
			)}/site-inspection-checklists/all?_=${timestamp}`;
			
			console.log(`EFFECT: Making API request to organization-wide endpoint: ${orgWideRequestUrl}`);
			
			return ajax
				.get(orgWideRequestUrl, headers)
				.pipe(
					mergeMap((ajaxResponse) => {
						console.log("EFFECT SUCCESS: org-site-inspection-checklist response:", ajaxResponse.response);
						return of(
							onSiteInspectionChecklistListReceive(
								ajaxResponse.response
							)
						);
					}),
					catchError((error) => {
						console.error("EFFECT ERROR: Error fetching with organization-wide endpoint:", error);
						// Return empty results instead of error
						return of(
							onSiteInspectionChecklistListReceive({
								results: [],
								totalNumberOfRecords: 0,
								totalNumberOfPages: 0,
								pageNumber: 1,
								pageSize: 0,
							})
						);
					})
				);
		})
	);
};

// This effect handles paged requests for organization-wide site inspection checklists
export const orgSiteInspectionChecklistPagedEffect = (action$, state$) => {
	console.log('orgSiteInspectionChecklistPagedEffect initialized');
	
	return action$.pipe(
		ofType(fromTypes.SITE_INSPECTION_CHECKLIST_PAGED_LIST_REQUESTED),
		filter(action => {
			console.log('SITE_INSPECTION_CHECKLIST_PAGED_LIST_REQUESTED action received:', action);
			
			// Log state structure to debug
			if (state$ && state$.value) {
				logStateStructure(state$.value);
			} else {
				console.log('State is not available');
			}
			
			// Log token status - continue even if missing
			const token = localStorage.getItem("token");
			console.log('Token value exists:', !!token);
			
			// Check which state structure has the organization info
			let organisationId = null;
			let currentOrganisation = null;
			
			if (state$.value.app && state$.value.app.currentOrganisation) {
				currentOrganisation = state$.value.app.currentOrganisation;
				console.log('Found organization in state.app.currentOrganisation');
			} else if (state$.value.organisations && state$.value.organisations.currentOrganisation) {
				currentOrganisation = state$.value.organisations.currentOrganisation;
				console.log('Found organization in state.organisations.currentOrganisation');
				
				// If currentOrganisation is a string/GUID, use it directly
				if (typeof currentOrganisation === 'string') {
					console.log('Current organization is a string/GUID:', currentOrganisation);
					organisationId = currentOrganisation;
				}
				// Otherwise, if it's an object but missing ID, try GUID
				else if (currentOrganisation && !currentOrganisation.id && currentOrganisation.guid) {
					console.log('Using organization GUID instead of ID:', currentOrganisation.guid);
					organisationId = currentOrganisation.guid;
				}
				// If it has an ID, use that
				else if (currentOrganisation && currentOrganisation.id) {
					organisationId = currentOrganisation.id;
				}
			}
			
			// Last resort - check if we can pull anything from mapStateToProps binding
			if (!organisationId && state$.value.organisations && state$.value.organisations.currentOrganisation) {
				const rawOrg = state$.value.organisations.currentOrganisation;
				if (typeof rawOrg === 'string') {
					console.log('Using raw organization GUID from state:', rawOrg);
					organisationId = rawOrg;
				}
			}
			
			if (!organisationId) {
				console.log('Could not determine organization ID from any source - skipping request');
				return false;
			}
			
			console.log('Using organization ID/GUID:', organisationId);

			if (!navigator.onLine) {
				console.log('No network connection - skipping request');
				return false;
			}

			console.log('All conditions passed - proceeding with request');
			return true;
		}),
		// Add delay to ensure state is populated
		delay(300),
		mergeMap(action => {
			console.log('Processing paged request with parameters:', action.payload);
			
			// Get the organization ID using our comprehensive approach
			let organisationId = null;
			
			// Check app.currentOrganisation first
			if (state$.value.app && state$.value.app.currentOrganisation) {
				const org = state$.value.app.currentOrganisation;
				if (org.id) {
					organisationId = org.id;
				} else if (org.guid) {
					organisationId = org.guid;
				}
			} 
			
			// If not found, check organisations.currentOrganisation
			if (!organisationId && state$.value.organisations && state$.value.organisations.currentOrganisation) {
				const org = state$.value.organisations.currentOrganisation;
				
				// If it's a string (GUID), use it directly
				if (typeof org === 'string') {
					organisationId = org;
				}
				// If it's an object with ID, use that
				else if (org && org.id) {
					organisationId = org.id;
				}
				// If it has a GUID but no ID, use the GUID
				else if (org && org.guid) {
					organisationId = org.guid;
				}
			}
			
			if (!organisationId) {
				console.error('Could not find organization ID in state');
				return of({
					type: fromTypes.SITE_INSPECTION_CHECKLIST_LIST_FAILED,
					payload: []
				});
			}
			
			console.log('Using organization ID/GUID for request:', organisationId);
			
			// Note: Using organization-wide endpoint without a site ID as requested
			const searchModel = action.payload;
			const organizationWideUrl = `${getOrganisationBaseUrl(state$.value)}/site-inspection-checklists/paged`;
			console.log('Using organization-wide URL without site ID:', organizationWideUrl);
			
			// Create more robust headers
			const token = localStorage.getItem("token");
			const headers = {
				"Content-Type": "application/json"
			};
			
			// Only add Authorization header if token exists
			if (token) {
				headers["Authorization"] = `Bearer ${token}`;
			} else {
				// Try using authHeaders helper instead
				Object.assign(headers, authHeaders());
			}
			
			// Use the more robust headers
			return ajax({
				url: organizationWideUrl,
				method: "POST",
				headers,
				body: searchModel
			}).pipe(
				map(ajaxResponse => {
					console.log('AJAX response received:', ajaxResponse);
					return {
						type: fromTypes.SITE_INSPECTION_CHECKLIST_LIST_RECEIVED,
						payload: ajaxResponse.response
					};
				}),
				catchError(error => {
					console.error('Error in orgSiteInspectionChecklistPagedEffect:', error);
					return of({
						type: fromTypes.SITE_INSPECTION_CHECKLIST_LIST_FAILED,
						payload: []
					});
				})
			);
		})
	);
};

// Export both effects as default
export default [orgSiteInspectionChecklistEffect, orgSiteInspectionChecklistPagedEffect]; 